import React from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

// Configs, contexts and utils
import { useConfiguration } from '../../context/configurationContext';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { formatMoney } from '../../util/currency';
import { types as sdkTypes } from '../../util/sdkLoader';
import { getListingsById } from '../../ducks/marketplaceData.duck';
import { isScrollingDisabled } from '../../ducks/ui.duck';

// Shared components
import { LayoutSingleColumn, ListingCard, NamedLink, Page } from '../../components';

// Containers
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import NotFoundPage from '../NotFoundPage/NotFoundPage';

// Same directory
import { getBlogArticle, getBlogCategory } from './blogArticles';
import {
  BlogText,
  BlogVisual,
  getArticleImage,
  getFirstListingImage,
  toPlainText,
} from './BlogShared';

import css from './BlogPage.module.css';

const { Money } = sdkTypes;

const LISTING_CARD_SIZES = '(max-width: 767px) 78vw, (max-width: 1023px) 33vw, 260px';

const searchLinkProps = categoryId =>
  categoryId ? { to: { search: `?pub_categoryLevel1=${categoryId}` } } : {};

const createSchema = ({ article, category, rootURL, marketplaceName, intl }) => {
  const url = `${rootURL}/blog/${article.slug}`;
  const organization = { '@type': 'Organization', name: marketplaceName, url: rootURL };

  return [
    {
      '@context': 'http://schema.org',
      '@type': 'Article',
      headline: article.title,
      description: article.metaDescription,
      inLanguage: 'fr-BE',
      datePublished: article.updated,
      dateModified: article.updated,
      author: organization,
      publisher: organization,
      mainEntityOfPage: url,
      articleSection: category.label,
    },
    {
      '@context': 'http://schema.org',
      '@type': 'FAQPage',
      mainEntity: article.faq.map(item => ({
        '@type': 'Question',
        name: item.q,
        acceptedAnswer: { '@type': 'Answer', text: toPlainText(item.a) },
      })),
    },
    {
      '@context': 'http://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { name: marketplaceName, item: rootURL },
        { name: intl.formatMessage({ id: 'BlogPage.breadcrumbBlog' }), item: `${rootURL}/blog` },
        { name: article.title, item: url },
      ].map((crumb, i) => ({ '@type': 'ListItem', position: i + 1, ...crumb })),
    },
  ];
};

/**
 * Blog article about a trade: short answer first, key facts, the real listings of the
 * trade category, then the guide, the FAQ and calls to book.
 *
 * @component
 * @param {Object} props
 * @param {Object?} props.staticContext set by the router during server-side rendering
 * @returns {JSX.Element}
 */
const BlogArticlePage = props => {
  const intl = useIntl();
  const config = useConfiguration();
  const { slug } = useParams();
  const article = getBlogArticle(slug);

  const scrollingDisabled = useSelector(state => isScrollingDisabled(state));
  const listingIds = useSelector(state => state.BlogPage.listingIdsBySlug[slug]) || [];
  const totalItems = useSelector(state => state.BlogPage.totalItemsBySlug[slug]) || 0;
  const listings = useSelector(state => getListingsById(state, listingIds));
  const averagePrice = useSelector(state => state.BlogPage.averagePriceBySlug?.[slug]);
  const categoryImages = useSelector(state => state.BlogPage.categoryImages);

  if (!article) {
    return <NotFoundPage staticContext={props.staticContext} />;
  }

  const category = getBlogCategory(article.category);
  // The article illustration first, else a listing photo of its topic (e.g. an epoxy training),
  // else a listing photo of its category
  const illustration = getArticleImage(article, {});
  const heroImage =
    illustration || getFirstListingImage(listings) || getArticleImage(article, categoryImages);
  const formattedAveragePrice = averagePrice
    ? formatMoney(intl, new Money(averagePrice.amount, averagePrice.currency))
    : null;
  const linkProps = searchLinkProps(article.category);
  const relatedArticles = article.related.map(getBlogArticle).filter(Boolean);

  const schema = createSchema({
    article,
    category,
    rootURL: config.marketplaceRootURL,
    marketplaceName: config.marketplaceName,
    intl,
  });

  const facts = [
    {
      key: 'formations',
      label: intl.formatMessage({ id: 'BlogPage.factFormations' }),
      value: totalItems || '–',
    },
    {
      key: 'price',
      label: intl.formatMessage({ id: 'BlogPage.factPrice' }),
      value: formattedAveragePrice || '–',
    },
    {
      key: 'payment',
      label: intl.formatMessage({ id: 'BlogPage.factPayment' }),
      value: intl.formatMessage({ id: 'BlogPage.factPaymentValue' }),
    },
    {
      key: 'place',
      label: intl.formatMessage({ id: 'BlogPage.factPlace' }),
      value: intl.formatMessage({ id: 'BlogPage.factPlaceValue' }),
    },
  ];

  const ctaLabel = article.category ? (
    <FormattedMessage id="BlogPage.seeCategoryFormations" values={{ category: category.label }} />
  ) : (
    <FormattedMessage id="BlogPage.seeAllFormations" />
  );

  return (
    <Page
      title={article.metaTitle}
      description={article.metaDescription}
      schema={schema}
      published={article.updated}
      updated={article.updated}
      openGraphType="article"
      facebookImages={heroImage ? [heroImage.large] : undefined}
      twitterImages={heroImage ? [heroImage.large] : undefined}
      scrollingDisabled={scrollingDisabled}
    >
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <div className={css.root}>
          <div className={css.container}>
            <div className={css.layout}>
              <article>
                <nav aria-label={intl.formatMessage({ id: 'BlogPage.breadcrumbLabel' })}>
                  <ol className={css.breadcrumbs}>
                    <li>
                      <NamedLink name="BlogPage">
                        <FormattedMessage id="BlogPage.breadcrumbBlog" />
                      </NamedLink>
                    </li>
                    {article.category ? (
                      <li>
                        <NamedLink name="BlogPage" to={{ search: `?metier=${article.category}` }}>
                          {category.label}
                        </NamedLink>
                      </li>
                    ) : null}
                  </ol>
                </nav>

                <span className={css.chip}>{category.label}</span>
                <h1 className={css.articleTitle}>{article.title}</h1>
                <p className={css.meta}>
                  <span>
                    <FormattedMessage id="BlogPage.byTeam" />
                  </span>
                  <span>
                    <FormattedMessage
                      id="BlogPage.updatedOn"
                      values={{
                        date: intl.formatDate(article.updated, {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric',
                        }),
                      }}
                    />
                  </span>
                </p>

                <BlogVisual
                  className={css.hero}
                  categoryId={article.category}
                  image={heroImage}
                  alt={article.title}
                  sizes="(max-width: 1023px) 100vw, 740px"
                />

                <section className={css.summary}>
                  <h2 className={css.summaryTitle}>
                    <FormattedMessage id="BlogPage.summaryTitle" />
                  </h2>
                  <p className={css.summaryText}>{article.summary}</p>
                </section>

                <dl className={css.facts}>
                  {facts.map(f => (
                    <div key={f.key} className={css.fact}>
                      <dt>{f.label}</dt>
                      <dd>{f.value}</dd>
                    </div>
                  ))}
                </dl>

                <section>
                  <h2 className={css.sectionTitle}>
                    <FormattedMessage id="BlogPage.listingsTitle" />
                  </h2>
                  {listings.length > 0 ? (
                    <div className={css.listings}>
                      {listings.map(l => (
                        <ListingCard
                          key={l.id.uuid}
                          className={css.listingCard}
                          listing={l}
                          renderSizes={LISTING_CARD_SIZES}
                        />
                      ))}
                    </div>
                  ) : (
                    <p className={css.noListings}>
                      <FormattedMessage id="BlogPage.noListings" />
                    </p>
                  )}
                  <NamedLink name="SearchPage" className={css.listingsLink} {...linkProps}>
                    {ctaLabel}
                  </NamedLink>
                </section>

                {article.sections.map(section => (
                  <section key={section.heading}>
                    <h2 className={css.sectionTitle}>{section.heading}</h2>
                    {(section.paragraphs || []).map(text => (
                      <p key={text} className={css.paragraph}>
                        <BlogText text={text} />
                      </p>
                    ))}
                    {section.list ? (
                      <ol className={css.steps}>
                        {section.list.map(item => (
                          <li key={item} className={css.step}>
                            <span>
                              <BlogText text={item} />
                            </span>
                          </li>
                        ))}
                      </ol>
                    ) : null}
                  </section>
                ))}

                <section>
                  <h2 className={css.sectionTitle}>
                    <FormattedMessage id="BlogPage.faqTitle" />
                  </h2>
                  <div className={css.faq}>
                    {article.faq.map((item, i) => (
                      <details key={item.q} className={css.faqItem} open={i === 0}>
                        <summary>{item.q}</summary>
                        <p className={css.faqAnswer}>{item.a}</p>
                      </details>
                    ))}
                  </div>
                </section>

                <section className={css.cta}>
                  <h2 className={css.ctaTitle}>
                    <FormattedMessage id="BlogPage.ctaTitle" />
                  </h2>
                  <p className={css.ctaText}>
                    <FormattedMessage id="BlogPage.ctaText" />
                  </p>
                  <NamedLink name="SearchPage" className={css.ctaButton} {...linkProps}>
                    {ctaLabel}
                  </NamedLink>
                </section>

                {relatedArticles.length > 0 ? (
                  <section>
                    <h2 className={css.sectionTitle}>
                      <FormattedMessage id="BlogPage.relatedTitle" />
                    </h2>
                    <div className={css.related}>
                      {relatedArticles.map(a => (
                        <NamedLink
                          key={a.slug}
                          className={css.relatedPost}
                          name="BlogArticlePage"
                          params={{ slug: a.slug }}
                        >
                          <BlogVisual
                            className={css.relatedVisual}
                            categoryId={a.category}
                            image={getArticleImage(a, categoryImages)}
                            alt={a.title}
                            sizes="(max-width: 767px) 84px, 240px"
                          />
                          <span>
                            <span className={css.chip}>{getBlogCategory(a.category).label}</span>
                            <span className={css.relatedTitle}>{a.title}</span>
                          </span>
                        </NamedLink>
                      ))}
                    </div>
                  </section>
                ) : null}
              </article>

              <aside className={css.sidebar}>
                <h2 className={css.sidebarTitle}>{ctaLabel}</h2>
                <div className={css.sidebarRow}>
                  <span>
                    <FormattedMessage id="BlogPage.factFormations" />
                  </span>
                  <strong>{totalItems || '–'}</strong>
                </div>
                <div className={css.sidebarRow}>
                  <span>
                    <FormattedMessage id="BlogPage.factPrice" />
                  </span>
                  <strong>{formattedAveragePrice || '–'}</strong>
                </div>
                <NamedLink name="SearchPage" className={css.sidebarButton} {...linkProps}>
                  <FormattedMessage id="BlogPage.book" />
                </NamedLink>
                <p className={css.sidebarNote}>
                  <FormattedMessage id="BlogPage.paymentNote" />
                </p>
              </aside>
            </div>
          </div>

          <div className={css.mobileBar}>
            <span className={css.mobileBarText}>
              {formattedAveragePrice ? (
                <FormattedMessage
                  id="BlogPage.fromPrice"
                  values={{ price: formattedAveragePrice }}
                />
              ) : (
                category.label
              )}
            </span>
            <NamedLink name="SearchPage" className={css.mobileBarButton} {...linkProps}>
              <FormattedMessage id="BlogPage.seeFormations" />
            </NamedLink>
          </div>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

export default BlogArticlePage;
